import { format } from "@cloudinary/url-gen/actions/delivery";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import { useMemo } from "react";

import { CATEGORY_HAUNT } from "@/components/game-categories/constants";
import { hasCategory } from "@/components/game-categories/helpers";
import { gameUrl } from "@/helpers/games";
import useCloudinary from "@/hooks/use-cloudinary";

import { type GameType, ImageTypeEnum } from "../types";

const HAUNT_WORD_REGEX = /haunt\w*/i;

export default function useMetaProps(game: GameType) {
  const cloudinary = useCloudinary();

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need cloudinary to re-render
  return useMemo(
    () => ({
      description: `Visit the ${game.name} attraction in ${game.location.locality.locality}, ${game.location.locality.region}. Discover ratings, reviews, and other important details about this immersive adventure.`,
      image: (() => {
        const img = (game.gameimageSet || []).find(
          (i) => i.imageType === ImageTypeEnum.IMAGE
        );
        return img
          ? cloudinary.image(img.imageId).delivery(format(auto())).toURL()
          : `${process.env.APP_URL}/images/share.jpg`;
      })(),
      title: (() => {
        const isHaunt =
          !!game.primaryCategory &&
          hasCategory(game.primaryCategory, CATEGORY_HAUNT);
        const pageTitleParts: string[] = [game.name];

        if (
          game.kind === "RER" &&
          !game.name.toLowerCase().includes("escape") &&
          !isHaunt
        ) {
          pageTitleParts.push("Escape Room");
        }

        // Add Haunted Attraction to the title if "*haunt*" isn't in the game title
        if (isHaunt && !HAUNT_WORD_REGEX.test(game.name)) {
          pageTitleParts.push("Haunted Attraction");
        }

        pageTitleParts.push("|");
        pageTitleParts.push(
          game.location.displayName || game.location.company.name
        );

        return pageTitleParts.join(" ");
      })(),
      url: process.env.APP_URL + gameUrl(game),
    }),
    [game]
  );
}
